<template>
  <section
    class="message-list-layout"
    :style="{
      padding: $arMediaQuery.pageContent.maxWidth('sm') ? '30px 18px' : '47px 59px',
    }"
  >
    <NuxtLayout name="custom">
      <MessageListSettingsModal
        v-if="currentMessageList"
        title="List Settings"
        :is-show="showMessageListSettingsModal"
        :message-list="currentMessageList"
        @close="showMessageListSettingsModal = false"
        @update="handleMessageListSettingsConfirm"
      />
      <StepLink
        text="Back to Lists"
        @click="handleBackLinkClick"
      />
      <ImportProgressBarModal
        v-if="isShowImportProgressBar"
        :progress="importProgressData.progress"
        :total-rows="importProgressData.totalRows"
        :rows-added="importProgressData.rowsAdded"
      />
      <div
        :class="[
          'message-list-top-wrapper',
          $arMediaQuery.pageContent.maxWidth('xs') && 'xs-max',
        ]"
      >
        <div
          :class="[
            'message-list-top-left-section',
            $arMediaQuery.pageContent.minWidth('sm') && 'sm-min',
          ]"
        >
          <div
            v-show="!showMessageListNameEdit"
            class="title-section"
          >
            <Heading
              class="title"
              type="h1"
              size="lg"
              :title="messageListName"
              @click="handleEditNameInputOpen"
            />
            <AM3Icon
              class="icon ml-10"
              name="edit"
              @click="handleEditNameInputOpen"
            />
            <Tag
              v-if="isAutoMessageList"
              v-tooltip.top="{
                content: 'Existing and future contacts that match your segment will automatically be added to this list',
              }"
              class="tag"
              text="Dynamic"
              type="purple"
            />
          </div>
          <div
            v-show="showMessageListNameEdit"
            class="edit-section"
          >
            <div class="edit-section-input">
              <input
                ref="edit-name-input"
                v-model="messageListNameEdit"
                data-vv-name="messageListNameEdit"
                class="edit-name-input"
                input-font-size="lg"
                @blur="handleEditNameInputBlur"
                @keyup.esc="handleEditNameInputBlur"
                @keyup.enter="handleEditNameInputEnter"
              >
              <span
                class="edit-name-input-done text-sm"
                @click="handleEditNameInputEnter"
              >
                <a>Done</a>
              </span>
            </div>
          </div>
        </div>
        <div class="message-list-top-right-section">
          <SplitDropdown
            v-if="showNewMessageButton"
            class="ml-2"
            :items="newMessageButtonOptions"
            text="New Message"
            icon-name="dialog"
            icon-distance="12px"
            toggle-dropdown-on-button-click
            :style="{
              minWidth: '195px',
            }"
            @select="item => item.action()"
            @button-click="() => { null }"
          />
          <ExpandIconButton
            v-if="currentMessageList"
            class="ml-2"
            icon-name="settings"
            @click="handleSettingsClick"
          />
        </div>
      </div>
      <AM3Snackbar
        v-if="noFacebookIntegration"
        variant="error"
        :style="{ marginTop: '10px' }"
        @click="handleIntegrationClick"
      >
        Integration error - <a>please reconnect your Facebook account</a>
      </AM3Snackbar>
      <hr
        class="border-t border-gray-200"
        :style="{
          marginTop: '22px',
          marginBottom: hasPendingTasks ? '10px' : '22px',
        }"
      >
      <AM3Snackbar
        v-if="hasPendingTasks"
        variant="warning"
      >
        {{ pendingTasksCopy }}
      </AM3Snackbar>
      <div
        v-if="isFetchingMessageList"
        class="loading-section"
      >
        <LoadingBubble />
      </div>
      <!-- <nuxt-child
        v-else-if="currentMessageList"
        @contactsCountUpdate="handleContactsCountUpdate"
      /> -->
      <slot />
    </NuxtLayout>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { toRaw } from 'vue'
import { mergeObjects, arraysEqualUnsorted } from '@/utils/helpers/'

export default {
  name: 'MessageListView',
  layout: 'default',

  data() {
    return {
      showMessageListSettingsModal: false,
      showMessageListNameEdit: false,
      messageListNameEdit: null,
      messageListFacebookPages: [], // Only populated if list contains FB Messenger
      contactsCount: 0,
      pendingTasksInterval: null,
      arEnableEmailSending: toRaw(this.$config).public.arEnableEmailSending,
      importStatusInterval: null,
      importProgressData: {
        oid: null,
        progress: 0,
        rowsAdded: 0,
        totalRows: 0,
      },
    }
  },

  computed: {
    ...mapState({
      currentMessageList: state => state.messageListModule.currentMessageList,
      isFetchingMessageList: state => state.messageListModule.isFetchingMessageList,
      isFetchingSubscribers: state => state.subscriberModule.isFetchingSubscribers,
      promoterOid: state => state.authModule.account?.promoterOid,
    }),
    ...mapGetters({
      pendingTasksForList: 'promoterTasksModule/pendingTasksForList',
      pendingExportTasksForMessageList: 'promoterTasksModule/pendingExportTasksForMessageList',
      pendingMassEditTasksForMessageList: 'promoterTasksModule/pendingMassEditTasksForMessageList',
      pendingCsvImportTasksForMessageList: 'promoterTasksModule/pendingCsvImportTasksForMessageList',
    }),

    isImportingContacts() {
      return (this.pendingMassEditTasksForMessageList(this.oid).length > 0
        || this.pendingCsvImportTasksForMessageList(this.oid).length > 0)
      && this.pendingExportTasksForMessageList(this.oid).length === 0
    },

    isShowImportProgressBar() {
      return this.importProgressData?.oid
    },

    oid() {
      return parseInt(this.$route.params['messagelist'], 10)
    },
    path() {
      return this.$route.path
    },

    pendingTasksCopy() {
      if (!(this.pendingMassEditTasksForMessageList(this.oid).length > 0
        || this.pendingCsvImportTasksForMessageList(this.oid).length > 0)
      && this.pendingExportTasksForMessageList(this.oid).length > 0) {
        return 'Export in progress. You\'ll receive an email soon.'
      }
      else if ((this.pendingMassEditTasksForMessageList(this.oid).length > 0
        || this.pendingCsvImportTasksForMessageList(this.oid).length > 0)
      && this.pendingExportTasksForMessageList(this.oid).length > 0) {
        return 'Multiple tasks in progress (Import and Export).'
      }
      return ''
    },

    hasPendingTasks() {
      return this.pendingTasksCopy && this.pendingTasksForList(this.oid).length > 0
    },

    messageListName() {
      if (!this.currentMessageList) return ''
      if (this.currentMessageList.meta.name) return this.currentMessageList.meta.name
      return this.currentMessageList.name
    },

    isAutoMessageList() {
      if (!this.currentMessageList) {
        return false
      }
      return this.currentMessageList.filterGroupOid ? true : false
    },
    noFacebookIntegration() {
      if (!this.currentMessageList || !this.currentMessageList.facebookMessenger) return false // Just return false if there's no FB messenger on this list
      if (!this.currentMessageList.meta || !this.currentMessageList.meta.facebookMessenger || !this.currentMessageList.meta.facebookMessenger.pageId) return false // If the list is missing a Facebook Page ID, return false
      if (!this.messageListFacebookPages || this.messageListFacebookPages.length < 1) return false // If there's no pages, return false (otherwise we flash the error while the pages are loaded)
      const targetPage = this.messageListFacebookPages.find(item => item.puid === this.currentMessageList.meta.facebookMessenger.pageId)
      if (!targetPage) return true // If the list's page can't be found, return true
      return targetPage.authorized === false // If the target page is not authorized, return true;
    },

    newMessageButtonOptions() {
      const options = []

      if (!this.currentMessageList) return []

      if (this.arEnableEmailSending && this.currentMessageList.email) {
        options.push({
          name: 'Email',
          icon: {
            name: 'email',
            color: this.$arStyle.color.email,
            height: '14px',
            width: '19px',
            wrapperStyle: {
              width: '19px',
            },
          },
          action: this.handleNewEmailMessageClick,
        })
      }

      if (this.currentMessageList.sms) {
        options.push({
          name: 'SMS',
          icon: {
            name: 'sms',
            color: this.$arStyle.color.sms,
            height: '19px',
            width: '18px',
            wrapperStyle: {
              width: '19px',
            },
          },
          action: this.handleNewSmsMessageClick,
        })
      }
      return options
    },

    showNewMessageButton() {
      if (!this.currentMessageList) return false
      if (!this.currentMessageList.sms && !this.currentMessageList.email) return false

      return true
    },
  },

  watch: {
    isImportingContacts: {
      handler: function (val) {
        if (val) {
          this.startImportStatusPolling()
        }
      },
      immediate: true,
    },
  },

  created() {
    this['messageListModule/RESET_CURRENT_MESSAGE_LIST']()
  },

  async mounted() {
    await this.fetchMessageList()
    if (this.currentMessageList) {
      this.messageListNameEdit = this.currentMessageList.name
    }
    if (this.currentMessageList && !!this.currentMessageList.facebookMessenger) {
      this.fetchFacebookIntegrations()
    }

    this['promoterTasksModule/START_POLLING_PENDING_TASKS']()

    this.checkPendingTasks()
  },

  beforeUnmount() {
    this['messageListModule/RESET_CURRENT_MESSAGE_LIST']()
  },

  methods: {
    ...mapActions([
      'messageListModule/FETCH_CURRENT_MESSAGE_LIST',
      'messageListModule/UPDATE_MESSAGE_LIST',
      'promoterTasksModule/START_POLLING_PENDING_TASKS',
      'FETCH_FACEBOOK_PAGES',
      'SHOW_CONFIRM',
      'messageListModule/FETCH_MESSAGE_LIST_PROGRESS_BY_OID',
    ]),
    ...mapMutations([
      'messageListModule/RESET_CURRENT_MESSAGE_LIST',
    ]),

    async fetchMessageListImportStatus() {
      if (!this.promoterOid || !this.oid) return

      const data = await this['messageList/FETCH_MESSAGE_LIST_PROGRESS_BY_OID']({
        messageListOid: this.oid,
      })

      // If data is empty, stop polling as import is complete
      if (!data || data.length === 0) {
        this.stopImportStatusPolling()
        this.importProgressData.oid = null
        this.fetchMessageList()
        return
      }

      const result = data[0]
      this.importProgressData = {
        oid: result?.oid || null,
        progress: result?.statusDetails?.progress || 0,
        rowsAdded: result?.statusDetails?.rowsProcessed || 0,
        totalRows: result?.statusDetails?.totalRows || 0,
      }
    },

    startImportStatusPolling() {
      this.stopImportStatusPolling()

      this.fetchMessageListImportStatus()

      this.importStatusInterval = setInterval(() => {
        this.fetchMessageListImportStatus()
      }, 10000)
    },

    stopImportStatusPolling() {
      if (this.importStatusInterval) {
        clearInterval(this.importStatusInterval)
        this.importStatusInterval = null
      }
    },

    async fetchFacebookIntegrations() {
      const pages = await this.FETCH_FACEBOOK_PAGES()
      if (pages) {
        this.messageListFacebookPages = pages
      }
    },

    getEditNameInputElem() {
      return this.$refs['edit-name-input']
    },

    fetchMessageList() {
      this['messageListModule/FETCH_CURRENT_MESSAGE_LIST']({ oid: this.oid })
    },
    handleContactsCountUpdate(val) {
      this.contactsCount = val
    },
    handleSettingsClick() {
      this.showMessageListSettingsModal = true
    },
    handleMessageListSettingsConfirm() {
      this.showMessageListSettingsModal = false
    },

    handleBackLinkClick() {
      this.$router.push({
        path: '/message-center/lists',
      })
    },

    handleEditNameInputOpen() {
      this.showMessageListNameEdit = true
      this.messageListNameEdit = this.messageListName
      this.$nextTick(() => {
        this.getEditNameInputElem().focus()
      })
    },

    handleEditNameInputBlur() {
      this.showMessageListNameEdit = false
    },

    handleIntegrationClick() {
      this.$router.push({
        path: `/settings/integrations`,
      })
    },

    async handleEditNameInputEnter() {
      if (!this.messageListNameEdit) {
        this.showMessageListNameEdit = false
        this.messageListNameEdit = this.messageListName
        return
      }
      const succeed = await this['messageListModule/UPDATE_MESSAGE_LIST']({
        oid: this.oid,
        changes: {
          name: this.messageListNameEdit,
        },
      })
      if (succeed) {
        this.showMessageListNameEdit = false
      }
    },

    handleNewEmailMessageClick() {
      this.createNewEmail()
    },

    handleNewSmsMessageClick() {
      this.createNewMessage('sms')
    },

    async createNewEmail() {
      if (this.$arMediaQuery.window.maxWidth('xs')) {
        const response = await this.SHOW_CONFIRM({
          messageHtml: `You are able to create and edit emails using your desktop or tablet device.`,
          hideConfirmButton: true,
          cancelButtonText: 'Back',
        })
      }
      else {
        this.$router.push({
          path: `/message-center/messages/email/new/basic`,
          query: {
            messageList: this.oid,
          },
        })
      }
    },

    createNewMessage(type) {
      if (type === 'sms') {
        this.$router.push({
          path: `/message-center/messages/sms/new`,
          query: {
            messageList: this.oid,
          },
        })
      }
    },

    checkPendingTasks() {
      this['promoterTasksModule/START_POLLING_PENDING_TASKS']()
    },
  },
}
</script>

<style scoped>
.message-list-layout .loading-section {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 200px;
}
.message-list-layout .message-list-top-wrapper {
  margin-top: 27px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
.message-list-layout .message-list-top-wrapper.xs-max { flex-direction: column; }
.message-list-layout .message-list-top-wrapper .message-list-top-left-section {
  display: flex;
  flex-direction: row;
  min-width: 0;
  align-items: center;
  width: 100%;
  margin-bottom: 12px;
  flex-grow: 1;
}
.message-list-layout .message-list-top-wrapper .message-list-top-left-section.sm-min { margin-bottom: 0; }
.message-list-layout .message-list-top-wrapper .message-list-top-right-section {
  display: flex;
  flex-shrink: 0;
}
.message-list-layout .title-section {
  display: flex;
  align-items: center;
  height: 40px;
  flex-direction: row;
  max-width: calc(100% - 30px);
}
.message-list-layout .title-section .title { cursor: pointer; }
.message-list-layout .title-section .icon {
  cursor: pointer;
  color: #DCDEE4;
  height: 16px;
}
.message-list-layout .title-section .tag {
  height: 30px;
  margin-left: 19px;
  padding: 0 24px;
}
.message-list-layout .edit-section {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.message-list-layout .edit-section .edit-section-input {
  display: flex;
  flex-direction: row;
  width: 100%;
}
.message-list-layout .edit-section .edit-section-input .edit-name-input {
  width: calc(100% - 35px);
  height: 40px;
  border: none;
  box-shadow: none;
  background: transparent;
  font-size: 30px;
  color: #54657E;
  border-bottom: 1px dashed #D9CCED;
  padding-bottom: 4px;
  outline: none;
}
.message-list-layout .edit-section .edit-section-input .edit-name-input-done {
  color: #7344c0;
  text-decoration: underline;
  cursor: pointer;
  margin: 0 0 0 12px;
  align-self: center;
}
.message-list-layout .edit-section .edit-section-input .input-confirm {
  width: 35px;
  display: inline-flex;
  margin-left: 8px;
}
</style>
